import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import {
  type WorkspaceSelection,
  type WorkspaceSelectorViewModel,
} from '@/features/inventory/admin/WorkspaceSelectorViewModel'
import type { AlertFactory } from '@/features/shell'
import { type Bundle } from '@/features/admin/bundle/Bundle'
import { validationContext } from 'validx'
import { type CreateBundleParams } from '@/features/admin/bundle/BundleStore'
import { type Id } from '@/infra/id'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import { type RefreshBundlesFn } from '@/features/admin/bundle/BundleSearchViewModel'

export class BulkDuplicateBundleDialogViewModel implements WorkspaceSelection {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The selected workspace
   */
  @observable
  accessor workspace: Workspace | undefined = undefined

  @observable
  accessor selectedBundles: Bundle[] = []

  @observable
  accessor incompatibleBundleItemIdsMap = new Map<Id, Set<Id>>()

  @observable
  accessor incompatibleBundleIds = new Set<Id>()

  @observable
  accessor compatibleBundles: CreateBundleParams[] = []

  readonly workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  constructor(
    public workspaceSelectorVm: WorkspaceSelectorViewModel,
    private readonly _newAlert: AlertFactory,
    private readonly _bundleStore: BundleStore,
    private readonly _refreshBundles: RefreshBundlesFn,
  ) {}

  @computed
  get incompatibleBundleList() {
    return this.selectedBundles.filter((bundle) =>
      this.incompatibleBundleIds.has(bundle.id),
    )
  }

  @computed
  get partiallyCompatibleBundleList() {
    return this.selectedBundles.filter((bundle) =>
      this.incompatibleBundleItemIdsMap.has(bundle.id),
    )
  }

  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.compatibleBundles = []
    this.incompatibleBundleIds.clear()
    this.incompatibleBundleItemIdsMap.clear()

    if (!workspace) {
      return
    }

    for (const bundle of this.selectedBundles) {
      const incompatibleBundle =
        bundle.baseItem.workspaceId &&
        bundle.baseItem.workspaceId !== workspace.id

      // we can't duplicate these bundles
      if (incompatibleBundle) {
        this.incompatibleBundleIds.add(bundle.id)
        continue
      }

      // we can duplicate these bundles but without these service items
      const incompatibleItems = new Set(
        bundle.includedItems.items
          .filter(
            (item) => item.workspaceId && item.workspaceId !== workspace.id,
          )
          .map((item) => item.inventoryItemId),
      )

      if (incompatibleItems.size) {
        this.incompatibleBundleItemIdsMap.set(bundle.id, incompatibleItems)
      }

      const includedItems = bundle.includedItems.items.filter(
        (item) =>
          !item.isBaseItem && !incompatibleItems.has(item.inventoryItemId),
      )

      this.compatibleBundles.push({
        name: bundle.name,
        description: bundle.description,
        baseItemId: bundle.baseItem.inventoryItemId,
        includedItems,
        price: bundle.price,
        workspaceId: workspace.id,
      })
    }
  }

  @action.bound
  open(bundles: Bundle[]) {
    this.selectedBundles = bundles
    this.setWorkspace(bundles[0].workspace)

    this.modal.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []
    this.compatibleBundles = []
    this.incompatibleBundleIds.clear()
    this.incompatibleBundleItemIdsMap.clear()
    this.workspace = undefined
  }

  submit = task.resolved(async () => {
    if (!this.workspaceValidation.reset().validate().isValid) {
      return
    }

    const failedPayloads = []

    for (const payload of this.compatibleBundles) {
      try {
        await this._bundleStore.create(payload)
      } catch {
        failedPayloads.push(payload)
      }
    }

    if (failedPayloads.length > 0) {
      this._newAlert()
        .withState('error')
        .saying(
          'Error',
          `There was an error while duplicating some bundles(${failedPayloads.length})`,
        )
        .autoDismiss()
        .show()
    }

    await this._refreshBundles(this.workspace)

    this.modal.close()
  })

  getIncompatibleItems(bundle: Bundle) {
    return bundle.includedItems.items.filter((item) =>
      this.incompatibleBundleItemIdsMap
        .get(bundle.id)
        ?.has(item.inventoryItemId),
    )
  }
}
